import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  Input,
  PageHeader,
  Skeleton,
} from "@oms/ui";
import { ApiClientError } from "@oms/api-client";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { useMemo, useState } from "react";
import { ConnectButton } from "@/components/integrations/connect-button";
import {
  CapabilityList,
  CapabilitySummary,
} from "@/components/integrations/capability-list";
import { ProviderLogo } from "@/components/integrations/provider-logo";
import { PageShell } from "@/components/pages/page-shell";
import { integrationsApi } from "@/lib/api";
import { findProvider, integrationQueryKeys } from "@/lib/integrations";
import { useAuthStore } from "@/stores/auth-store";

type ConnectSearch = {
  provider: string;
};

type ConnectMode = "app_auth" | "api_key";

export const Route = createFileRoute("/app/integrations/connect")({
  validateSearch: (search: Record<string, unknown>): ConnectSearch => ({
    provider:
      typeof search.provider === "string" && search.provider.trim() !== ""
        ? search.provider
        : "",
  }),
  component: ConnectIntegrationPage,
});

function ConnectIntegrationPage() {
  const navigate = useNavigate();
  const { provider: providerId } = Route.useSearch();
  const organization = useAuthStore((s) => s.organization);
  const [store, setStore] = useState("");
  const [consumerKey, setConsumerKey] = useState("");
  const [consumerSecret, setConsumerSecret] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const providersQuery = useQuery({
    queryKey: integrationQueryKeys.providers(),
    queryFn: async () => {
      const response = await integrationsApi.listProviders();
      return response.data ?? [];
    },
  });

  const provider = providersQuery.data
    ? findProvider(providersQuery.data, providerId)
    : undefined;

  const availableModes = useMemo((): ConnectMode[] => {
    if (!provider?.connectModes?.length) {
      return provider?.authType === "oauth2" || provider?.authType === "app_auth"
        ? ["app_auth"]
        : [];
    }
    return provider.connectModes.filter(
      (mode): mode is ConnectMode => mode === "app_auth" || mode === "api_key",
    );
  }, [provider]);

  const [connectMode, setConnectMode] = useState<ConnectMode>("app_auth");

  const activeMode = availableModes.includes(connectMode)
    ? connectMode
    : (availableModes[0] ?? "app_auth");

  const isManualConnect = activeMode === "api_key";

  const connectMutation = useMutation({
    mutationFn: async () => {
      if (!organization?.id || !provider) {
        throw new Error("Missing organization or provider");
      }
      const response = await integrationsApi.connect({
        provider: provider.provider,
        store: store.trim(),
        connectMode: isManualConnect ? "api_key" : "app_auth",
        consumerKey: isManualConnect ? consumerKey.trim() : undefined,
        consumerSecret: isManualConnect ? consumerSecret.trim() : undefined,
      });
      return response.data;
    },
    onSuccess: (data) => {
      if (data?.redirectUrl) {
        window.location.href = data.redirectUrl;
        return;
      }
      if (data?.connectionId) {
        void navigate({
          to: "/app/integrations",
          search: {
            status: "connected",
            connectionId: data.connectionId,
          },
        });
        return;
      }
      setErrorMessage("Provider did not return a connection result.");
    },
    onError: (error) => {
      if (error instanceof ApiClientError) {
        setErrorMessage(error.message);
        return;
      }
      setErrorMessage(
        error instanceof Error ? error.message : "Failed to start connection",
      );
    },
  });

  const storeLabel =
    provider?.authType === "oauth2"
      ? "Store domain"
      : provider?.authType === "app_auth" || isManualConnect
        ? "Store URL"
        : "Store identifier";

  const storePlaceholder =
    provider?.authType === "oauth2"
      ? "your-store or your-store.myshopify.com"
      : provider?.authType === "app_auth" || isManualConnect
        ? "https://your-store.com or your-store.com"
        : "Enter the store URL or ID from your provider";

  return (
    <PageShell className="max-w-3xl">
      <Link
        to="/app/integrations"
        className="inline-flex items-center gap-2 text-sm font-medium text-text-secondary hover:text-text"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to integrations
      </Link>

      <PageHeader
        title={provider ? `Connect ${provider.displayName}` : "Connect store"}
        description="Authorize Ecordio to access your storefront. Credentials stay encrypted on the platform."
      />

      {providersQuery.isLoading ? (
        <Skeleton className="h-80 rounded-2xl" />
      ) : null}

      {providersQuery.isError ? (
        <ErrorState
          description="Could not load provider details."
          onRetry={() => void providersQuery.refetch()}
        />
      ) : null}

      {!providersQuery.isLoading && !provider ? (
        <EmptyState
          title="Provider not found"
          description="Choose a provider from the integrations catalog to start a connection."
          action={
            <Link to="/app/integrations">
              <span className="text-sm font-medium text-brand-600 hover:text-brand-700">
                Browse providers
              </span>
            </Link>
          }
        />
      ) : null}

      {provider ? (
        <Card>
          <CardHeader
            title={provider.displayName}
            description={
              provider.description ??
              `${provider.authType.toUpperCase()} connection`
            }
          />
          <CardBody className="space-y-6">
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <ProviderLogo
                  provider={provider.provider}
                  name={provider.displayName}
                />
                <CapabilitySummary capabilities={provider.capabilities} />
              </div>
              <CapabilityList capabilities={provider.capabilities} />
            </div>

            {!organization?.id ? (
              <EmptyState
                variant="inline"
                title="No active organization"
                description="Switch to a workspace before connecting a store."
              />
            ) : (
              <form
                className="space-y-4"
                onSubmit={(event) => {
                  event.preventDefault();
                  setErrorMessage(null);
                  if (!store.trim()) {
                    setErrorMessage("Enter your store domain or identifier.");
                    return;
                  }
                  if (isManualConnect) {
                    if (!consumerKey.trim() || !consumerSecret.trim()) {
                      setErrorMessage("Enter both the consumer key and secret.");
                      return;
                    }
                  }
                  connectMutation.mutate();
                }}
              >
                {availableModes.length > 1 ? (
                  <div className="space-y-2">
                    <span className="block text-sm font-medium text-text-secondary">
                      Connection method
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {availableModes.includes("app_auth") ? (
                        <button
                          type="button"
                          className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                            activeMode === "app_auth"
                              ? "border-brand-600 bg-brand-50 text-brand-700"
                              : "border-border text-text-secondary hover:border-border-strong"
                          }`}
                          onClick={() => setConnectMode("app_auth")}
                        >
                          Authorize in store
                        </button>
                      ) : null}
                      {availableModes.includes("api_key") ? (
                        <button
                          type="button"
                          className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                            activeMode === "api_key"
                              ? "border-brand-600 bg-brand-50 text-brand-700"
                              : "border-border text-text-secondary hover:border-border-strong"
                          }`}
                          onClick={() => setConnectMode("api_key")}
                        >
                          Manual API keys
                        </button>
                      ) : null}
                    </div>
                    <p className="text-sm text-text-muted">
                      {isManualConnect
                        ? "Generate REST API keys in WooCommerce → Settings → Advanced → REST API, then paste them here."
                        : "You will be redirected to your store to approve access."}
                    </p>
                  </div>
                ) : null}

                <div className="space-y-2">
                  <label
                    htmlFor="store"
                    className="block text-sm font-medium text-text-secondary"
                  >
                    {storeLabel}
                  </label>
                  <Input
                    id="store"
                    name="store"
                    value={store}
                    onChange={(event) => setStore(event.target.value)}
                    placeholder={storePlaceholder}
                    autoComplete="off"
                    required
                  />
                </div>

                {isManualConnect ? (
                  <>
                    <div className="space-y-2">
                      <label
                        htmlFor="consumerKey"
                        className="block text-sm font-medium text-text-secondary"
                      >
                        Consumer key
                      </label>
                      <Input
                        id="consumerKey"
                        name="consumerKey"
                        value={consumerKey}
                        onChange={(event) => setConsumerKey(event.target.value)}
                        placeholder="ck_..."
                        autoComplete="off"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <label
                        htmlFor="consumerSecret"
                        className="block text-sm font-medium text-text-secondary"
                      >
                        Consumer secret
                      </label>
                      <Input
                        id="consumerSecret"
                        name="consumerSecret"
                        type="password"
                        value={consumerSecret}
                        onChange={(event) =>
                          setConsumerSecret(event.target.value)
                        }
                        placeholder="cs_..."
                        autoComplete="off"
                        required
                      />
                    </div>
                  </>
                ) : null}

                {errorMessage ? (
                  <p className="text-sm text-error">{errorMessage}</p>
                ) : null}

                <ConnectButton
                  loading={connectMutation.isPending}
                  disabled={!organization?.id}
                  label={
                    isManualConnect ? "Connect store" : "Authorize connection"
                  }
                  loadingLabel={isManualConnect ? "Connecting…" : "Redirecting…"}
                />
              </form>
            )}
          </CardBody>
        </Card>
      ) : null}
    </PageShell>
  );
}
